import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { convertToDateJakartaTime, toIndoFormat } from "../../helper/helper"
import { DeliveryPhotoDialog } from "./DeliveryPhotoDialog"
import { ImageIcon, ImageOffIcon } from "./TodayDeliveryIcons"
import { DeliveryRow, statusPillClass } from "./todayDeliveryModel"

type Props = {
    row: DeliveryRow
    millCoords?: [number, number]
    onDetailClick: (row: DeliveryRow) => void
}

const NO_VALUE = '—'

/** Every row shares the market day, so the year would just be noise. */
const DAY_PATTERN = 'DD MMM'

export const TodayDeliveryRow: React.FC<Props> = ({ row, millCoords, onDetailClick }) => {
    const { t } = useTranslation()
    const [photoOpen, setPhotoOpen] = useState(false)
    // Only a truck photo opens the dialog; a weight receipt proves nothing about
    // where the truck was.
    const canOpenPhoto = row.photoKind === 'truck'

    return (
        <div className="ds-grid-table__row today-delivery__row" data-contract-id={row.contractId}>
            <span className="ds-grid-table__cell--num" style={{ textAlign: 'left' }}>{row.contractId}</span>

            <span className="today-delivery__where">
                <span className="today-delivery__village">{row.village ?? row.supplierName ?? NO_VALUE}</span>
                <span className="today-delivery__area">{row.area ?? t('area_not_resolved')}</span>
            </span>

            <span className="today-delivery__photo-cell">
                {canOpenPhoto
                    ? <button
                        type="button"
                        className={`today-delivery__photo today-delivery__photo--button${row.photoUrl ? ' today-delivery__photo--thumb' : ''}`}
                        title={t('truck_photo')}
                        aria-label={t('truck_photo')}
                        onClick={(e) => { setPhotoOpen(true); e.stopPropagation() }}
                    >
                        {row.photoUrl
                            ? <img src={row.photoUrl} alt="" loading="lazy" />
                            : <ImageIcon />}
                    </button>
                    : <span className="today-delivery__photo today-delivery__photo--empty" title={t('no_truck_photo')}>
                        <ImageOffIcon color="var(--ds-dashed-text)" />
                    </span>}
            </span>

            <span className="today-delivery__date">
                {row.deliveryDate
                    ? convertToDateJakartaTime(row.deliveryDate, DAY_PATTERN)
                    : NO_VALUE}
            </span>

            <span>
                <span className={`ds-pill ds-pill--state ${statusPillClass(row.statusKind)}`}>
                    <span className="ds-pill__dot" />
                    {row.isNac
                        ? t('adjustment').toUpperCase()
                        : (row.statusLabelKey ? t(row.statusLabelKey) : row.status).toUpperCase()}
                </span>
            </span>

            <span className="ds-grid-table__cell--num">
                {row.isNac || row.pricePerKg == null ? NO_VALUE : toIndoFormat(row.pricePerKg)}
            </span>

            <span className="ds-grid-table__cell--end">
                <button
                    type="button"
                    className="ds-btn ds-btn--primary ds-btn--sm"
                    onClick={(e) => { onDetailClick(row); e.stopPropagation() }}
                >
                    {t('detail')}
                </button>
            </span>

            {canOpenPhoto && (
                <DeliveryPhotoDialog
                    row={row}
                    millCoords={millCoords}
                    isOpen={photoOpen}
                    onClose={() => setPhotoOpen(false)}
                />
            )}
        </div>
    )
}
